import React, { useEffect, useState } from "react";
import { Typography, message, Button, Modal, Spin, Select, Tag, Avatar } from "antd";
import PaymentServ from "../service/PaymentServ";
import OrdersServ from "../service/OrdersServ";
import { usePreference } from "../context/PreferenceContext";
import {
    IconCreditCard,
    IconHistory,
    IconX,
    IconReload,
    IconPackage,
    IconCheck,
    IconTruck
} from "@tabler/icons-react";
import QuickFilter from "../component/ui/QuickFilter";
import DateRangePicker from "../component/ui/DateRangePicker";
import { formatUnit } from "../util/unitFormatter";
import "../css/sellerDashboard.css";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import DataTable from "../component/ui/DataTable";
import Breadcrumbs from "../component/Breadcrumbs";

dayjs.extend(isBetween);

const { Text } = Typography;

const PaymentHistory = () => {
    const { formatPrice, t } = usePreference();
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter states
    const [quickFilter, setQuickFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [methodFilter, setMethodFilter] = useState("ALL");

    // Order detail modal state
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const fetchHistory = async (showLoading = true) => {
        try {
            if (showLoading && transactions.length === 0) setLoading(true);
            const cached = localStorage.getItem('cached_buyer_payment_history');
            if (cached && showLoading) {
                try {
                    const parsed = JSON.parse(cached);
                    setTransactions(parsed);
                    setFilteredTransactions(parsed);
                    setLoading(false); // Stop skeleton immediately
                } catch (e) { }
            }

            const res = await PaymentServ.getHistory();
            const data = res.data || res || [];

            localStorage.setItem('cached_buyer_payment_history', JSON.stringify(data));
            setTransactions(data);
            setFilteredTransactions(data);
        } catch (error) {
            console.error("Fetch Payment History Error:", error);
            message.error("Failed to load payment history");
        } finally {
            setLoading(false);
        }
    };

    // 🧠 Handle Filtering
    useEffect(() => {
        let result = [...transactions];

        // 1. Date Range Filter
        if (dateRange && dateRange[0] && dateRange[1]) {
            const start = dateRange[0].startOf('day');
            const end = dateRange[1].endOf('day');
            result = result.filter(item => {
                const itemDate = dayjs(item.createdAt);
                return itemDate.isBetween(start, end, null, '[]');
            });
        }

        // 2. Status Filter
        if (statusFilter && statusFilter !== 'ALL') {
            result = result.filter(item => {
                const s = item.status?.toLowerCase();
                if (statusFilter === 'SUCCESS') return s === 'captured' || s === 'success';
                if (statusFilter === 'FAILED') return s === 'failed';
                if (statusFilter === 'PENDING') return !['captured', 'success', 'failed'].includes(s);
                return true;
            });
        }

        // 3. Payment Method Filter
        if (methodFilter && methodFilter !== 'ALL') {
            result = result.filter(item => {
                const method = (item.paymentMethod || item.paymentType || item.method || "online").toLowerCase();
                if (methodFilter === 'COD') {
                    return method === 'cod';
                }
                if (methodFilter === 'ONLINE') {
                    return method !== 'cod';
                }
                return true;
            });
        }

        setFilteredTransactions(result);
    }, [transactions, dateRange, statusFilter, methodFilter]);

    const handleQuickFilterChange = (key, range) => {
        setQuickFilter(key);
        setDateRange(range);
    };

    const handleDateRangeChange = (range) => {
        setQuickFilter(null); // Clear quick filter if manual range selected
        setDateRange(range);
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    const handleViewOrder = async (orderId) => {
        try {
            setOrderLoading(true);
            setOrderModalOpen(true);
            const res = await OrdersServ.single(orderId);
            const order = res?.data || res;
            setSelectedOrder(order);
        } catch (error) {
            console.error("Fetch Order Error:", error);
            message.error("Failed to load order details");
            setOrderModalOpen(false);
        } finally {
            setOrderLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const map = {
            ordered: "default",
            confirmed: "#722ed1",
            packed: "#1890ff",
            picked_up: "#faad14",
            out_for_delivery: "#faad14",
            delivered: "#52c41a",
            cancelled: "#ff4d4f",
        };
        return map[status?.toLowerCase()] || "default";
    };

    const getStatusLabel = (status) => {
        const s = status?.toLowerCase();
        const map = {
            ordered: t('ordered', 'Ordered'),
            confirmed: t('confirmed', 'Confirmed'),
            packed: t('packed', 'Packed'),
            picked_up: t('picked_up', 'Out for Delivery'),
            out_for_delivery: t('picked_up', 'Out for Delivery'),
            delivered: t('delivered', 'Delivered'),
            cancelled: t('cancelled', 'Cancelled'),
        };
        return map[s] || status;
    };

    const getStatusStep = (status) => {
        const current = status?.toLowerCase();
        if (!current || ['cancelled', 'delivery_missed', 'returned', 'refund_completed'].includes(current)) {
            return -1;
        }
        // Map legacy/alias statuses to canonical flow step
        const mapped = (current === 'out_for_delivery' || current === 'shipped') ? 'picked_up'
            : (current === 'assigned') ? 'confirmed'   // driver assigned → Confirmed step
                : current;
        const sequence = ["ordered", "confirmed", "packed", "picked_up", "delivered"];
        const index = sequence.indexOf(mapped);
        return index > -1 ? index : 0;
    };

    const columns = React.useMemo(() => [

        {
            title: t('order_id', 'Order ID'),
            dataIndex: "orders",
            key: "orders",
            render: (orders) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {orders?.map((order) => (
                        <Text
                            strong
                            key={order._id}
                            onClick={() => handleViewOrder(order._id)}
                            style={{ cursor: 'pointer', color: '#1e293b', whiteSpace: 'nowrap' }}
                        >
                            #{order.orderId}
                        </Text>
                    ))}
                </div>
            ),
            width: 120,
        },
        {
            title: t('status', "Status"),
            dataIndex: "status",
            key: "status",
            render: (status, record) => {
                const s = status?.toLowerCase();
                const orderStatus = record.orders?.[0]?.paymentStatus?.toLowerCase();
                
                const isRefunded = orderStatus === "refunded";
                const isSuccess = s === "captured" || s === "success" || orderStatus === "completed" || orderStatus === "success" || orderStatus === "paid";
                const isFailed = s === "failed" || orderStatus === "failed";
                
                const color = isRefunded ? 'magenta' : isSuccess ? 'green' : isFailed ? 'red' : 'orange';
                const label = isRefunded ? 'REFUNDED' : isSuccess ? 'SUCCESS' : isFailed ? 'FAILED' : 'PENDING';
                return (
                    <Tag color={color} style={{ margin: 0, fontWeight: 700, borderRadius: '4px', textTransform: 'uppercase' }}>
                        {label}
                    </Tag>
                );
            },
            width: 130,
        },
        {
            title: t('method', "Method"),
            dataIndex: "method",
            key: "method",
            render: (method, record) => {
                const { formatPaymentDisplay } = require('../util/paymentFormatter');
                const orderMethod = record.orders?.[0]?.paymentMethod || record.orders?.[0]?.paymentType;
                const displayMethod = formatPaymentDisplay(
                    record.paymentMethod || record.paymentType || method || orderMethod || "Online",
                    null,
                    record.paymentSubMethod || record.orders?.[0]?.paymentSubMethod
                );
                return (
                    <div style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        background: '#eff6ff',
                        color: '#2563eb',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase'
                    }}>
                        {displayMethod}
                    </div>
                );
            },
            width: 100,
        },
        {
            title: t('amount', "Amount"),
            dataIndex: "amount",
            key: "amount",
            render: (amount, record) => {
                const order = record.orders?.[0];
                const orderPayable = order?.payableAmount !== undefined ? order.payableAmount : (order?.totalOrderAmount || order?.totalAmount);
                const displayAmount = orderPayable !== undefined ? orderPayable : amount;
                return (
                    <Text strong style={{ color: '#1e293b', fontSize: '15px' }}>
                        {formatPrice(displayAmount)}
                    </Text>
                );
            },
            width: 120,
        },
        {
            title: t('transaction_id', "Transaction ID"),
            dataIndex: "razorpayPaymentId",
            key: "razorpayPaymentId",
            render: (id, record) => {
                let displayId = id;
                if (!displayId && record._id) {
                    const numericId = parseInt(record._id.slice(-8), 16).toString().padStart(10, '0');
                    const orderMethod = record.orders?.[0]?.paymentMethod || record.orders?.[0]?.paymentType;
                    const method = record.method || orderMethod || "ONLINE";
                    displayId = `NZN-${method.toUpperCase()}-${numericId}`;
                }
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Ref ID</span>
                        <Text style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                            {displayId || "N/A"}
                        </Text>
                    </div>
                );
            },
            width: 220,
        },
        {
            title: t('date_time', "Date & Time"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => (
                <div className="pcu-flex-column">
                    <Text strong style={{ fontSize: '13px' }}>{dayjs(date).format('DD MMM YYYY')}</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(date).format('hh:mm A')}</Text>
                </div>
            ),
            width: 160,
        },
        {
            title: t('action', 'Action'),
            key: 'action',
            render: (_, record) => {
                const firstOrderId = record.orders?.[0]?._id;
                return (
                    <Button
                        type="default"
                        size="small"
                        disabled={!firstOrderId}
                        onClick={() => firstOrderId && handleViewOrder(firstOrderId)}
                        style={{ borderRadius: 4, fontWeight: 500, color: '#003a8c', borderColor: '#d9d9d9' }}
                    >
                        View
                    </Button>
                );
            },
            width: 90,
            align: 'center',
        }
    ], [t, formatPrice, handleViewOrder]);

    return (
        <div className="orders-page-container pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div style={{ marginTop: 16, background: "#fff", borderRadius: 16, border: "1px solid #f1f5f9", padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                        <div style={{
                            width: 48, height: 48, background: "#eef2ff", borderRadius: 12,
                            display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                            <IconCreditCard size={24} color="#4f46e5" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e293b" }}>{t('payment_history', 'Payment History')}</h2>
                            <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>
                                {filteredTransactions.length} transactions listed
                            </Text>
                        </div>
                    </div>

                    <div style={{ flex: 1, display: "flex", justifyContent: isMobile ? "flex-start" : "flex-end", gap: 12, overflowX: "auto", alignItems: "center", flexWrap: "wrap" }}>
                        <QuickFilter
                            value={quickFilter}
                            onChange={handleQuickFilterChange}
                            label={null}
                        />
                        <DateRangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            label={null}
                        />
                        <Select
                            placeholder="All Status"
                            style={{ minWidth: 140 }}
                            onChange={val => setStatusFilter(val || "ALL")}
                            value={statusFilter}
                            allowClear
                        >
                            <Select.Option value="ALL">All Status</Select.Option>
                            <Select.Option value="SUCCESS">Success</Select.Option>
                            <Select.Option value="PENDING">Pending</Select.Option>
                            <Select.Option value="FAILED">Failed</Select.Option>
                        </Select>
                        <Select
                            placeholder="All Methods"
                            style={{ minWidth: 140 }}
                            onChange={val => setMethodFilter(val || "ALL")}
                            value={methodFilter}
                            allowClear
                        >
                            <Select.Option value="ALL">All Methods</Select.Option>
                            <Select.Option value="COD">COD</Select.Option>
                            <Select.Option value="ONLINE">Online</Select.Option>
                        </Select>
                        <Button
                            type="default"
                            onClick={() => {
                                setQuickFilter(null);
                                setDateRange(null);
                                setStatusFilter("ALL");
                                setMethodFilter("ALL");
                            }}
                            style={{ borderRadius: 8, fontWeight: 500 }}
                        >
                            Reset
                        </Button>
                        <Button
                            icon={<IconReload size={16} />}
                            onClick={() => fetchHistory(true)}
                            loading={loading}
                            style={{ borderRadius: 8, fontWeight: 500 }}
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="pcu-border-top pcu-margin-x-n24 pcu-padding-x-24 pcu-padding-top-16">
                    <DataTable
                        columns={columns}
                        data={filteredTransactions}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            onChange: (page) => {
                                const pageContent = document.querySelector('.page-content');
                                if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                else window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                        locale={{ emptyText: "No payment transactions found." }}
                    />
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .premium-timeline {
                    display: flex;
                    justify-content: space-between;
                    position: relative;
                    padding: 0 20px;
                }
                .premium-timeline .timeline-item {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    z-index: 1;
                }
                .premium-timeline .timeline-item .line {
                    position: absolute;
                    top: 18px;
                    left: 50%;
                    width: 100%;
                    height: 3px;
                    background: #f1f5f9;
                    z-index: -1;
                }
                .premium-timeline .timeline-item:last-child .line {
                    display: none;
                }
                .premium-timeline .timeline-item.past .line {
                    background: #6366f1;
                }
                .premium-timeline .timeline-item .dot-wrapper {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: #fff;
                    border: 2px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #94a3b8;
                    transition: all 0.3s ease;
                }
                .premium-timeline .timeline-item.past .dot-wrapper {
                    background: #6366f1;
                    border-color: #6366f1;
                    color: #fff;
                }
                .premium-timeline .timeline-item.current .dot-wrapper {
                    background: #fff;
                    border-color: #6366f1;
                    color: #6366f1;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
                }
                .premium-timeline .timeline-item .label {
                    margin-top: 12px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .premium-timeline .timeline-item.past .label, .premium-timeline .timeline-item.current .label {
                    color: #1e293b;
                }

                .premium-alert-banner {
                    display: flex;
                    gap: 16px;
                    padding: 16px;
                    border-radius: 12px;
                    background: #fdf2f2;
                    border: 1px solid #fee2e2;
                }
                .premium-alert-banner.warning {
                    background: #fffbeb;
                    border-color: #fef3c7;
                }
                .premium-alert-banner .icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .premium-alert-banner.error .icon { color: #ef4444; }
                .premium-alert-banner.warning .icon { color: #f59e0b; }
                .premium-alert-banner .content { display: flex; flex-direction: column; gap: 4px; }
                .premium-alert-banner .title { font-size: 15px; color: #1e293b; }
                .premium-alert-banner .desc { font-size: 13px; color: #64748b; }

                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 20px;
                }
                .info-card {
                    padding: 20px;
                    background: #f8fafc;
                    border-radius: 16px;
                    border: 1px solid #f1f5f9;
                }
                .info-card .card-label {
                    font-size: 10px;
                    font-weight: 800;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 16px;
                }
                .info-card .detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }
                .info-card .detail-row:last-child { margin-bottom: 0; }
                .info-card .detail-row .label { font-size: 12px; color: #64748b; }
                .info-card .detail-row .value { font-size: 13px; font-weight: 700; color: #1e293b; text-align: right; }
                .info-card .detail-row .value.address { max-width: 120px; color: #64748b; font-weight: 500; font-size: 12px; }

                .items-section .section-title {
                    font-size: 16px;
                    font-weight: 800;
                    color: #1e293b;
                    margin-bottom: 16px;
                }
                .order-item-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px 0;
                    border-bottom: 1px solid #f1f5f9;
                }
                .order-item-row:last-child { border-bottom: none; }
                .order-item-row .item-details { flex-grow: 1; }
                .order-item-row .item-name { font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
                .order-item-row .item-meta { display: flex; gap: 12px; font-size: 12px; color: #64748b; }
                .order-item-row .item-price { text-align: right; }
                .order-item-row .unit-price { font-size: 12px; color: #94a3b8; }
                .order-item-row .total-price { font-size: 14px; font-weight: 800; color: #1e293b; }

                .summary-card {
                    background: #fff;
                    border: 1px solid #f1f5f9;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.02);
                }
                .summary-content { padding: 20px; display: flex; flex-direction: column; gap: 10px; background: #f8fafc; }
                .summary-row { display: flex; justify-content: space-between; font-size: 13px; color: #64748b; }
                .summary-row.savings { color: #f97316; font-weight: 700; }
                .summary-footer { padding: 20px; display: flex; justify-content: space-between; align-items: center; background: #fff; }
                .payable-label { font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; }
                .payable-amount { font-size: 24px; font-weight: 900; color: #6366f1; }
                .badge.success {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    background: #f0fdf4;
                    color: #15803d;
                    font-size: 10px;
                    font-weight: 800;
                    border-radius: 8px;
                    letter-spacing: 0.5px;
                }
            `}} />

            {/* Order Details Modal */}
            <Modal
                title={selectedOrder ? `${t('order_details', 'Order Details')} #${selectedOrder?.orderId}` : t('order_details', 'Order Details')}
                open={orderModalOpen}
                onCancel={() => { setOrderModalOpen(false); setSelectedOrder(null); }}
                footer={[
                    <Button key="close" onClick={() => { setOrderModalOpen(false); setSelectedOrder(null); }}>
                        {t('close', 'Close')}
                    </Button>
                ]}
                width={800}
            >
                {orderLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                        <Spin size="large" />
                    </div>
                ) : selectedOrder && (
                    <>
                        {/* Status Timeline */}
                        <div className="pcu-margin-bottom-32">
                            {selectedOrder.status === 'cancelled' || selectedOrder.status === 'return_requested' || selectedOrder.status === 'returned' || selectedOrder.status === 'refund_completed' ? (
                                <div className={`premium-alert-banner ${selectedOrder.status === 'cancelled' ? 'error' : 'warning'}`}>
                                    <div className="icon">
                                        {selectedOrder.status === 'cancelled' ? <IconX size={20} /> : <IconReload size={20} />}
                                    </div>
                                    <div className="content">
                                        <Text strong className="title">
                                            {selectedOrder.status === 'cancelled' ? t('order_cancelled', 'Order Cancelled') :
                                                selectedOrder.status === 'refund_completed' ? t('refund_completed', 'Refund Completed') : t('return_in_progress', 'Return In Progress')}
                                        </Text>
                                        <Text className="desc">
                                            {t('current_status', 'Current Status')}: {selectedOrder.status?.replace("_", " ")?.toUpperCase()}
                                        </Text>
                                    </div>
                                </div>
                            ) : (
                                <div className="premium-timeline">
                                    {[
                                        { key: 'ordered', label: t('ordered', 'Ordered'), icon: <IconPackage size={16} /> },
                                        { key: 'confirmed', label: t('confirmed', 'Confirmed'), icon: <IconCheck size={16} /> },
                                        { key: 'packed', label: t('packed', 'Packed'), icon: <IconHistory size={16} /> },
                                        { key: 'picked_up', label: t('picked_up', 'Out for Delivery'), icon: <IconTruck size={16} /> },
                                        { key: 'delivered', label: t('delivered', 'Delivered'), icon: <IconCheck size={16} /> }
                                    ].map((step, idx) => {
                                        const currentStep = getStatusStep(selectedOrder.status);
                                        const isPast = idx < currentStep;
                                        const isCurrent = idx === currentStep;
                                        return (
                                            <div key={idx} className={`timeline-item ${isPast ? 'past' : isCurrent ? 'current' : ''}`}>
                                                <div className="line"></div>
                                                <div className="dot-wrapper">
                                                    <div className="dot">{step.icon}</div>
                                                </div>
                                                <div className="label">{step.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Information Grid */}
                        <div className="info-grid pcu-margin-bottom-32">
                            <div className="info-card">
                                <div className="card-label">{t('order_info', 'Order Information')}</div>
                                <div className="detail-row">
                                    <span className="label">Order ID</span>
                                    <span className="value">#{selectedOrder.orderId}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Placed On</span>
                                    <span className="value">{dayjs(selectedOrder.createdAt).format('DD MMM YYYY, hh:mm A')}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Delivery Type</span>
                                    <Tag color={selectedOrder.deliveryType === 'Normal' ? 'blue' : 'orange'} className="pcu-margin-0">
                                        {selectedOrder.deliveryType?.toUpperCase() || 'NORMAL'}
                                    </Tag>
                                </div>
                            </div>

                            <div className="info-card">
                                <div className="card-label">{t('customer_delivery', 'Customer & Delivery')}</div>
                                <div className="detail-row">
                                    <span className="label">Name</span>
                                    <span className="value">{selectedOrder.deliveryAddress?.name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Phone</span>
                                    <span className="value">{selectedOrder.deliveryAddress?.phone}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Address</span>
                                    <span className="value address">{[selectedOrder.deliveryAddress?.street, selectedOrder.deliveryAddress?.city].filter(Boolean).join(', ')}</span>
                                </div>
                            </div>

                            <div className="info-card">
                                <div className="card-label">{t('payment_details', 'Payment Details')}</div>
                                <div className="detail-row">
                                    <span className="label">Method</span>
                                    <span className="value">
                                        {selectedOrder.creditsUsed > 0 ? ((selectedOrder.codAmount === 0 && selectedOrder.payableAmount === 0) ? 'CREDIT' : `CREDIT + ${(selectedOrder.paymentMethod || selectedOrder.paymentType || 'COD').toUpperCase()}`) : (selectedOrder.paymentMethod || selectedOrder.paymentType || 'COD').toUpperCase()}
                                    </span>
                                </div>
                                {selectedOrder.paymentCollectionMethod && (
                                    <div className="detail-row">
                                        <span className="label">Collected Via</span>
                                        <span className="value pcu-color-purple">{selectedOrder.paymentCollectionMethod}</span>
                                    </div>
                                )}
                                {selectedOrder.transactionId && (
                                    <div className="detail-row">
                                        <span className="label">{selectedOrder.transactionId.startsWith('NZN-') ? 'Ref ID' : 'Txn ID'}</span>
                                        <span className="value">{selectedOrder.transactionId}</span>
                                    </div>
                                )}
                                <div className="detail-row">
                                    <span className="label">Status</span>
                                    <Tag color={selectedOrder.paymentStatus === 'captured' || selectedOrder.paymentStatus === 'success' ? 'green' : 'gold'} className="pcu-margin-0">
                                        {(selectedOrder.paymentStatus || 'pending').toUpperCase()}
                                    </Tag>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Payable Amount</span>
                                    <span className="value pcu-color-purple">{formatPrice(selectedOrder.totalOrderAmount || selectedOrder.totalAmount)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Items Section */}
                        <div className="items-section pcu-margin-bottom-32">
                            <h4 className="section-title">{t('items_ordered', 'Items Ordered')}</h4>
                            <div className="items-list">
                                {(selectedOrder.items || []).map((item, idx) => (
                                    <div key={idx} className="order-item-row">
                                        <div className="item-img">
                                            <Avatar shape="square" size={64} src={item.imageUrl || item.product?.imageUrl} style={{ borderRadius: '12px' }} />
                                        </div>
                                        <div className="item-details">
                                            <div className="item-name">{item.name || item.product?.name}</div>
                                            <div className="item-meta">
                                                <span>Qty: <b>{item.quantity}</b></span>
                                                {item.selectedOptions && Object.entries(item.selectedOptions).map(([k, v]) => (
                                                    <span key={k}>{k}: <b>{formatUnit(v, k)}</b></span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="item-price">
                                            <div className="unit-price">{formatPrice(item.price)}</div>
                                            <div className="total-price">{formatPrice(item.price * item.quantity)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Price Summary Card */}
                        <div className="summary-card">
                            <div className="summary-content">
                                <div className="summary-row">
                                    <span>Items Subtotal</span>
                                    <span>{formatPrice((selectedOrder.totalOrderAmount || selectedOrder.totalAmount) - (selectedOrder.deliveryCharge || 50))}</span>
                                </div>
                                <div className="summary-row">
                                    <span>Delivery Charges</span>
                                    <span className="pcu-color-success">+ {formatPrice(selectedOrder.deliveryCharge || 50)}</span>
                                </div>
                                {(selectedOrder.walletUsedAmount > 0 || selectedOrder.creditsUsed > 0) && (
                                    <div className="summary-row savings">
                                        <span>Wallet/Credits Used</span>
                                        <span>- {formatPrice(selectedOrder.walletUsedAmount || selectedOrder.creditsUsed)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="summary-footer">
                                <div className="footer-left">
                                    <div className="payable-label">{(selectedOrder.paymentMethod || selectedOrder.paymentType) === 'COD' ? t('cod_payable', 'COD Payable Amount') : t('total_paid', 'Total Amount Paid')}</div>
                                    <div className="payable-amount">{formatPrice(selectedOrder.codAmount !== undefined ? selectedOrder.codAmount : selectedOrder.payableAmount)}</div>
                                </div>
                                <div className="footer-right">
                                    <div className="badge success">
                                        <IconCheck size={14} /> SECURE TRANSACTION
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default PaymentHistory;
